import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/client';
import { SpriteIcon } from '@/components/SpriteIcon';
import { useConfirm } from '@/components/ConfirmDialog';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  permissionAction,
  permissionsByCategory,
  useRolePermissions,
  useSetRolePermissions,
} from './useRoles';

/**
 * Replaces `Pages/Auth/RolePermissions.cshtml` at `/auth/rolepermissions`.
 *
 * A tab per role, each holding every permission the application defines as a
 * checkbox grouped by the area it governs. The Razor page posted one form per
 * role; this keeps that boundary — a save applies to the selected role only, and
 * sends the full set rather than a delta, so it is idempotent.
 *
 * Edits are held locally until saved. Switching tabs with unsaved changes warns
 * rather than discarding them silently, which the Razor page did not do: its
 * tabs were `data-bs-toggle` and swapping one threw away whatever had been
 * ticked.
 */
export function RolePermissionsPage() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const location = useLocation();
  const { data, isLoading, isError } = useRolePermissions();
  const savePermissions = useSetRolePermissions();

  /** Role deep-linked from the roles grid's key button, if any. */
  const requestedRole = (location.state as { role?: string } | null)?.role;

  const [activeRole, setActiveRole] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 200);
  const [saved, setSaved] = useState(false);

  const roles = useMemo(() => data?.roles ?? [], [data]);

  const current = useMemo(
    () => roles.find((role) => role.name === activeRole) ?? roles[0] ?? null,
    [roles, activeRole],
  );

  /**
   * Which role's grants are currently loaded into `selected`.
   *
   * Load the selection when the *role* changes — not whenever `current` changes
   * identity. `current` is derived from the query data, so it is a new object
   * after every refetch, and a save invalidates the query by design. Keying the
   * reload on identity therefore overwrote whatever had been ticked since the
   * last fetch, silently, mid-edit.
   */
  const loadedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!current) return;
    setActiveRole((name) => name ?? current.name);

    if (loadedFor.current !== current.name) {
      loadedFor.current = current.name;
      setSelected(new Set(current.permissions));
      setSaved(false);
    }
  }, [current]);

  // Honour a deep link from the roles grid once the roles have arrived.
  useEffect(() => {
    if (requestedRole && roles.some((role) => role.name === requestedRole)) {
      setActiveRole(requestedRole);
    }
  }, [requestedRole, roles]);

  const dirty = useMemo(() => {
    if (!current) return false;
    const original = new Set(current.permissions);
    if (original.size !== selected.size) return true;
    for (const permission of selected) if (!original.has(permission)) return true;
    return false;
  }, [current, selected]);

  const categories = useMemo(() => {
    const all = data?.allPermissions ?? [];
    const term = debouncedSearch.trim().toLowerCase();
    const matching = term ? all.filter((p) => p.toLowerCase().includes(term)) : all;
    return permissionsByCategory(matching);
  }, [data, debouncedSearch]);

  /** Only what the search currently shows, so the bulk buttons match the view. */
  const visiblePermissions = useMemo(
    () => categories.flatMap(([, permissions]) => permissions),
    [categories],
  );

  const switchRole = useCallback(
    async (name: string) => {
      // Warning rather than danger: nothing is deleted, the edits in progress
      // are simply dropped.
      if (dirty) {
        const confirmed = await confirm({
          variant: 'warning',
          title: t('Message.Confirm', 'Are you sure?'),
          message: t('RolePermissions.DiscardChanges', 'Discard the unsaved changes to this role?'),
          confirmLabel: t('RolePermissions.Discard', 'Discard changes'),
        });
        if (!confirmed) return;
      }

      setActiveRole(name);
      setSearch('');
    },
    [confirm, dirty, t],
  );

  const toggle = useCallback((permission: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(permission)) next.delete(permission);
      else next.add(permission);
      return next;
    });
  }, []);

  const setAllVisible = useCallback(
    (checked: boolean) => {
      setSelected((current) => {
        const next = new Set(current);
        for (const permission of visiblePermissions) {
          if (checked) next.add(permission);
          else next.delete(permission);
        }
        return next;
      });
    },
    [visiblePermissions],
  );

  const saveError =
    savePermissions.error instanceof ApiError
      ? ((savePermissions.error.problem as { errors?: string[] } | null)?.errors?.join(' ') ??
        savePermissions.error.message)
      : null;

  const handleSave = useCallback(() => {
    if (!current) return;
    savePermissions.mutate(
      { roleName: current.name, permissions: Array.from(selected) },
      { onSuccess: () => setSaved(true) },
    );
  }, [current, selected, savePermissions]);

  if (isError) {
    return (
      <div className="alert alert-danger m-3" role="alert">
        {t('RolePermissions.LoadError', 'Could not load role permissions. Please try again.')}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <span className="spinner-border spinner-border-sm me-2" role="status" />
        {t('Loading', 'Loading...')}
      </div>
    );
  }

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h1 className="h3 mb-1">{t('RolePermissions.Title', 'Role permissions')}</h1>
          <p className="text-muted mb-0">
            {t('RolePermissions.Subtitle', 'What each role is allowed to do.')}
          </p>
        </div>
        <div className="d-flex align-items-center gap-3">
          {/* Fixed-height slot: the save outcome appears beside the button rather
              than as a banner above the card, so nothing below it ever moves.
              A message that shifts the page is a message that moves the control
              you are about to click next. */}
          <div
            className="fs-sm text-truncate text-end"
            role="status"
            aria-live="polite"
            // Capped and single-line: a long server error must not wrap and grow
            // the header, which would move the Save button beside it.
            style={{ maxWidth: '28rem', whiteSpace: 'nowrap' }}
          >
            {saveError ? (
              <span className="text-danger" title={saveError}>
                {saveError}
              </span>
            ) : saved && !dirty ? (
              <span className="text-success d-inline-flex align-items-center">
                <SpriteIcon name="check" size={16} className="sa-icon me-1" />
                {t('RolePermissions.Saved', 'Permissions saved.')}
              </span>
            ) : dirty ? (
              <span className="text-muted">
                {t('RolePermissions.Unsaved', 'Unsaved changes')}
              </span>
            ) : null}
          </div>

          <button
            type="button"
            className="btn btn-primary d-inline-flex align-items-center"
            onClick={handleSave}
            disabled={!dirty || savePermissions.isPending}
          >
            <SpriteIcon name="save" size={16} className="sa-icon me-2" />
            {savePermissions.isPending ? t('Saving', 'Saving…') : t('Save', 'Save')}
          </button>
        </div>
      </div>

      <div className="row g-3 mb-4">
        {[
          { key: 'roles', label: t('RolePermissions.TotalRoles', 'Roles'), value: roles.length },
          {
            key: 'permissions',
            label: t('RolePermissions.TotalPermissions', 'Permissions'),
            value: data?.allPermissions.length ?? 0,
          },
          {
            key: 'granted',
            label: t('RolePermissions.GrantedToRole', 'Granted to this role'),
            value: selected.size,
          },
        ].map((tile) => (
          <div className="col-12 col-md-4" key={tile.key}>
            <div className="card shadow-sm h-100">
              <div className="card-body text-center">
                <h5 className="text-muted mb-2 fs-sm">{tile.label}</h5>
                <h2 className="mb-0">{tile.value}</h2>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <ul className="nav nav-tabs mb-4" role="tablist">
            {roles.map((role) => (
              <li className="nav-item" role="presentation" key={role.roleId}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={current?.name === role.name}
                  className={`nav-link d-inline-flex align-items-center${
                    current?.name === role.name ? ' active' : ''
                  }`}
                  onClick={() => void switchRole(role.name)}
                >
                  <SpriteIcon name="shield" size={16} className="sa-icon me-2" />
                  {role.name}
                  <span className="badge bg-primary ms-2">
                    {current?.name === role.name ? selected.size : role.permissions.length}
                  </span>
                </button>
              </li>
            ))}
          </ul>

          {current && (
            <>
              <div className="alert alert-light d-flex align-items-start" role="note">
                <SpriteIcon name="info" size={20} className="sa-icon me-2 flex-shrink-0" />
                <div>
                  <h5 className="mb-1">
                    {t('RolePermissions.RoleHeading', '{{name}} role', { name: current.name })}
                  </h5>
                  <p className="mb-0 text-muted">
                    {current.description ||
                      t('RolePermissions.NoDescription', 'No description for this role.')}
                  </p>
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-md-6">
                  <div className="input-group">
                    <span className="input-group-text">
                      <SpriteIcon name="search" size={16} />
                    </span>
                    <input
                      type="search"
                      className="form-control"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder={t('RolePermissions.Search', 'Search permissions…')}
                      aria-label={t('RolePermissions.Search', 'Search permissions…')}
                    />
                  </div>
                </div>
                <div className="col-md-6 text-end mt-2 mt-md-0">
                  {/* Bulk actions apply to what the search leaves visible, so a
                      filtered "select all" cannot silently grant hidden ones. */}
                  <button
                    type="button"
                    className="btn btn-outline-primary me-2"
                    onClick={() => setAllVisible(true)}
                  >
                    {t('RolePermissions.SelectAll', 'Select all')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setAllVisible(false)}
                  >
                    {t('RolePermissions.DeselectAll', 'Deselect all')}
                  </button>
                </div>
              </div>

              {categories.length === 0 && (
                <p className="text-muted text-center py-4 mb-0">
                  {t('RolePermissions.NoMatches', 'No permissions match this search')}
                </p>
              )}

              {categories.map(([category, permissions]) => (
                <div className="mb-4" key={category}>
                  <h5 className="border-bottom pb-2 mb-3 d-flex align-items-center">
                    <SpriteIcon name="folder" size={18} className="sa-icon me-2" />
                    {category}
                    <span className="badge bg-secondary ms-2">
                      {permissions.filter((p) => selected.has(p)).length}/{permissions.length}
                    </span>
                  </h5>
                  <div className="row">
                    {permissions.map((permission) => (
                      <div className="col-md-6 col-xl-4" key={permission}>
                        <div className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`perm-${permission}`}
                            checked={selected.has(permission)}
                            onChange={() => toggle(permission)}
                          />
                          <label className="form-check-label" htmlFor={`perm-${permission}`}>
                            {permissionAction(permission)}
                            <span className="d-block fs-sm text-muted">{permission}</span>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div className="text-end border-top pt-3">
                <button
                  type="button"
                  className="btn btn-primary btn-lg d-inline-flex align-items-center"
                  onClick={handleSave}
                  disabled={!dirty || savePermissions.isPending}
                >
                  <SpriteIcon name="save" size={18} className="sa-icon me-2" />
                  {savePermissions.isPending
                    ? t('Saving', 'Saving…')
                    : t('RolePermissions.SaveRole', 'Save {{name}} permissions', {
                        name: current.name,
                      })}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
